import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Calendar, MessageSquare, Users, FileText, Video, PenTool } from "lucide-react";

const HeroSection: React.FC = () => {
  const navigate = useNavigate();
  const [currentScreen, setCurrentScreen] = useState(1);
  const [hasTyped, setHasTyped] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      // Auto-advance screens every 2.5 seconds, but only up to screen 4
      const interval = setInterval(() => {
        setCurrentScreen(prev => {
          if (prev < 4) {
            if (prev === 1) {
              // Trigger typewriter animation when moving to screen 2
              setTimeout(() => setHasTyped(true), 100);
            }
            return prev + 1;
          }
          clearInterval(interval);
          return prev;
        });
      }, 2500);

      return () => clearInterval(interval);
    }, 4000); // Start after 4 seconds

    return () => clearTimeout(timer);
  }, []);
  
  return (
    <section className="py-6 md:py-10 relative overflow-hidden bg-white">
      {/* Subtle background */}
      <div className="absolute inset-0 z-0 opacity-20">
        <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#FA8C00] blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-[#4ba3c7] blur-3xl"></div>
      </div>
      
      <div className="w-full px-3 sm:px-4 lg:px-6 xl:px-8 relative z-10">
        <div className="flex flex-col lg:flex-row items-stretch gap-8 lg:gap-0">
          {/* Left column - Content */}
          <div className="lg:w-[85%] xl:w-[82%] lg:pr-8 mb-6 lg:mb-0">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-2xl sm:text-3xl md:text-4xl lg:text-4xl xl:text-5xl font-bold leading-tight mb-3 md:mb-5 text-gray-800 text-left px-2 sm:px-0"
            >
              <span className="block">All-in-one AI platform for</span>
              <span className="text-[#FA8C00] italic">thriving youth programs.</span>
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-base sm:text-lg md:text-xl leading-relaxed text-gray-600 mb-5 md:mb-6 text-left px-2 sm:px-0"
            >
              Streamline your entire operation with powerful scheduling, billing, communication, and student management tools — designed specifically for activity businesses like yours.
            </motion.p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3 mb-5 md:mb-6 px-2 sm:px-0 justify-items-start">
              {[
                { icon: <Users className="h-7 w-7 text-[#FA8C00]" />, title: "Global Search", desc: "Find any student, class, or family instantly" },
                { icon: <MessageSquare className="h-7 w-7 text-[#4ba3c7]" />, title: "Parent Portal", desc: "Reduces your emails by 70%" },
                { icon: <Calendar className="h-7 w-7 text-[#7ad4a0]" />, title: "Smart Scheduling", desc: "Everything connected in one view" },
                { icon: <FileText className="h-7 w-7 text-[#F75C57]" />, title: "Clear Dashboard", desc: "See what you need, when you need it" },
                { icon: <Video className="h-7 w-7 text-[#8B5CF6]" />, title: "HiveShare Video", desc: "Secure video sharing with families" },
                { icon: <PenTool className="h-7 w-7 text-[#10B981]" />, title: "Digital Forms", desc: "Paperless registration & e-signatures" }
              ].map((feature, index) => (
                <motion.div 
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 + index * 0.1 }}
                  className="flex items-start space-x-3 bg-white p-3 rounded-xl shadow-sm border border-gray-100 hover:shadow-lg hover:scale-[1.02] transition-all duration-300 group w-full justify-start"
                >
                  <div className="mt-1 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
                    {feature.icon}
                  </div>
                  <div className="text-left">
                    <h3 className="font-semibold mb-1 text-base transition-colors duration-300 group-hover:text-[#FA8C00] text-left">{feature.title}</h3>
                    <p className="text-sm text-gray-600 text-left">{feature.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-col gap-3 px-2 sm:px-0"
            >
              <Button 
                size="default" 
                className="w-full text-sm py-5 px-6 bg-gradient-to-r from-[#FA8C00] to-[#FA8C00]/90 hover:from-[#FA8C00]/90 hover:to-[#FA8C00]/80 transform transition-all duration-300 hover:scale-[1.02] hover:shadow-xl active:scale-[0.98] group rounded-xl shadow-lg" 
                onClick={() => navigate("/business-trial")}
              >  
                <span className="flex items-center gap-2 font-semibold">
                  <span className="transition-transform duration-300 group-hover:scale-110">
                    Start your free trial
                  </span>
                  <motion.span
                    className="inline-block"
                    animate={{ x: [0, 4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                  >
                    →
                  </motion.span>
                </span>
              </Button>
              <Button 
                variant="outline" 
                className="w-full text-sm py-5 px-6 border-2 border-[#FA8C00] text-[#FA8C00] hover:bg-[#FA8C00]/10 hover:border-[#FA8C00]/80 transform transition-all duration-300 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] group rounded-xl backdrop-blur-sm bg-white/80" 
                onClick={() => navigate("/pricing")}
              >
                <span className="font-semibold transition-transform duration-300 group-hover:scale-105">
                  See our transparent pricing
                </span>
              </Button>
            </motion.div>
            <p className="text-xs text-gray-500 mt-3 text-left">
              14-day trial. No credit card required.
            </p>
          </div>
          
          {/* Right column - AI Assistant Demo */}
          <div className="lg:w-[62%] xl:w-[65%] relative flex items-center justify-end">
            <div className="w-full max-w-md">
              <div className="relative bg-white rounded-xl shadow-xl overflow-hidden p-6" style={{
                boxShadow: '0 0 15px rgba(250, 140, 0, 0.2)',
                animation: currentScreen > 1 ? 'pulse 2s infinite' : 'none'
              }}>
                <div className="space-y-4">
                  
                  {/* Section 1: Welcome */}
                  <div className={`transition-all duration-600 ${currentScreen >= 1 ? 'opacity-100 transform-none' : 'opacity-0 translate-y-4'} text-center`}>
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 bg-white p-1">
                      <img src="https://imagedelivery.net/ylmXutM6_iJT3CT9AqBdjQ/b997351c-a1e3-429f-2fca-1ac1cbbfbf00/public" alt="Hivetivity Logo" className="w-10 h-10 object-contain" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">Ask Hivetivity AI</h3>
                    <p className="text-sm text-gray-600">Use Ask Hivetivity AI to query your business data — scheduling, payments, attendance and more — in plain English. Instant insights when you need answers fast.</p>
                    
                    {/* Loading Spinner */}
                    <div className={`mt-4 transition-opacity duration-300 ${currentScreen === 1 ? 'opacity-100' : 'opacity-0'}`}>
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-2 h-2 bg-[#FA8C00] rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                        <div className="w-2 h-2 bg-[#4ba3c7] rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                        <div className="w-2 h-2 bg-[#FA8C00] rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">Preparing demo...</p>
                    </div>
                  </div>

                  {/* Section 2: User Input */}
                  <div className={`transition-all duration-600 ${currentScreen >= 2 ? 'opacity-100 transform-none' : 'opacity-0 translate-y-4'}`}>
                    <div className="flex items-start space-x-2 bg-gray-50 rounded-lg p-3">
                      <div className="w-6 h-6 bg-[#4ba3c7] rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="bg-white rounded p-2 shadow-sm border">
                          <p className={`text-gray-800 text-xs ${hasTyped ? 'typewriter-animate' : ''}`} style={{
                            overflow: hasTyped ? 'hidden' : 'visible',
                            borderRight: hasTyped ? '2px solid #4ba3c7' : 'none',
                            whiteSpace: hasTyped ? 'nowrap' : 'normal',
                            animation: hasTyped ? 'typewriter 2.2s steps(50, end), blink 1s step-end infinite' : 'none'
                          }}>
                            How many students do I have between the ages of 5 and 10?
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: AI Response */}
                  <div className={`transition-all duration-600 ${currentScreen >= 3 ? 'opacity-100 transform-none' : 'opacity-0 translate-y-4'}`}>
                    <div className="flex items-start space-x-2">
                      <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{
                        background: 'linear-gradient(135deg, #FA8C00 0%, #4ba3c7 100%)'
                      }}>
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"></path>
                        </svg>
                      </div>
                      <div className="bg-gradient-to-r from-orange-50 to-blue-50 rounded p-3 border border-orange-200 flex-1">
                        <p className="text-gray-800 font-medium text-xs mb-1">
                          Your organization has <span className="text-[#4ba3c7] font-bold">15 students</span> between ages 5-10 (<span className="text-[#FA8C00] font-bold">21%</span> of total).
                        </p>
                        
                        {/* Student Names */}
                        <div>
                          <p className="text-xs font-medium text-gray-600 mb-1">Students include:</p>
                          <div className="flex flex-wrap gap-1">
                            <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs">Emma Johnson (7)</span>
                            <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs">Liam Smith (9)</span>
                            <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs">Sophia Davis (6)</span>
                            <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs">Noah Wilson (8)</span>
                            <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs">Olivia Brown (5)</span>
                            <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs">Ethan Miller (10)</span>
                            <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs">Ava Garcia (7)</span>
                            <span className="bg-blue-100 text-blue-800 px-1 py-0.5 rounded text-xs">Mason Lee (9)</span>
                            <span className="bg-gray-300 text-gray-600 px-1 py-0.5 rounded text-xs">+7 more</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 4: Suggested Questions */}
                  <div className={`transition-all duration-600 ${currentScreen >= 4 ? 'opacity-100 transform-none' : 'opacity-0 translate-y-4'}`}>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs font-medium text-gray-600 mb-2">Try asking:</p>
                      <div className="flex flex-wrap gap-2">
                        <div className="bg-white hover:bg-orange-50 border border-gray-200 hover:border-orange-300 text-gray-700 hover:text-orange-700 px-2 py-1 rounded text-xs transition-all duration-200">
                          What's my average class size?
                        </div>
                        <div className="bg-white hover:bg-orange-50 border border-gray-200 hover:border-orange-300 text-gray-700 hover:text-orange-700 px-2 py-1 rounded text-xs transition-all duration-200">
                          Show enrollment by program
                        </div>
                        <div className="bg-white hover:bg-orange-50 border border-gray-200 hover:border-orange-300 text-gray-700 hover:text-orange-700 px-2 py-1 rounded text-xs transition-all duration-200">
                          Which students joined this month?
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

            <style jsx>{`
              @keyframes typewriter {
                from { width: 0; }
                to { width: 100%; }
              }
              
              @keyframes blink {
                0%, 50% { border-color: transparent; }
                51%, 100% { border-color: #4ba3c7; }
              }
              
              @keyframes pulse {
                0%, 100% {
                  box-shadow: 0 0 15px rgba(250, 140, 0, 0.2);
                }
                50% {
                  box-shadow: 0 0 25px rgba(250, 140, 0, 0.4);
                }
              }
            `}</style>
          </div>
        </div>
      </div>
      
    </section>
  );
};

export default HeroSection;